/**
 * Copyright © Adobe, Inc. All rights reserved.
 */

import { Files } from '@adobe/aio-sdk';
import { FileRecord, FileMetadata } from './types';

/**
 * FileRepository class for managing file-based storage operations
 * Provides CRUD operations for JSON files in a specified directory
 */
class FileRepository {
  private readonly filepath: string;
  private files: any = null;

  /**
   * Creates a new FileRepository instance
   * @param filepath - The base directory path for file operations
   */
  constructor(filepath: string) {
    this.filepath = filepath;
  }

  /**
   * Lists all files in the repository directory
   * @returns Promise<FileRecord[]> Array of file records
   */
  async list(): Promise<FileRecord[]> {
    const filesLib = await this.getFiles();
    const results: FileRecord[] = [];

    const existingFiles = (await this.metadata()) as FileMetadata[];
    if (existingFiles.length) {
      for (const file of existingFiles) {
        const buffer = await filesLib.read(`${file.name}`);
        const data = JSON.parse(buffer.toString());

        // Add file system timestamps
        results.push({
          ...data,
          createdAt: file.creationTime.toISOString(),
          updatedAt: file.lastModified.toISOString(),
        });
      }
    }

    return results;
  }

  /**
   * Lists file metadata without reading file contents
   * Provides a lightweight alternative to list() for performance-critical operations
   * @param id - Optional ID of a specific file to get metadata for
   * @returns Promise<FileMetadata | FileMetadata[]> Single file metadata if id provided, array of all files otherwise
   */
  async metadata(id?: string): Promise<FileMetadata | FileMetadata[]> {
    const filesLib = await this.getFiles();

    if (id) {
      const filepath = `${this.filepath}/${id}.json`;
      return await filesLib.getProperties(filepath);
    }

    return await filesLib.list(`${this.filepath}/`);
  }

  /**
   * Loads a specific file by ID
   * @param id - The ID of the file to load
   * @returns Promise<FileRecord> The loaded file record or empty object if not found
   */
  async load(id: string = ''): Promise<FileRecord> {
    const filepath = `${this.filepath}/${id}.json`;
    const filesLib = await this.getFiles();

    const existingFile = await filesLib.list(filepath);
    if (existingFile.length) {
      const buffer = await filesLib.read(filepath);
      const data = JSON.parse(buffer.toString());

      // Get file system properties to add timestamps
      const fileProps = await filesLib.getProperties(filepath);

      return {
        ...data,
        createdAt: fileProps.creationTime.toISOString(),
        updatedAt: fileProps.lastModified.toISOString(),
      };
    }

    return {} as FileRecord;
  }

  /**
   * Saves a file record to the repository
   * @param payload - The data to save
   * @param id - Optional ID for the file (sanitized to alphanumeric + underscore, takes precedence over payload.id)
   * @param overwrite - Optional flag to control file write behavior:
   *                    - true: Replace file entirely with payload (no merge)
   *                    - false (default): Merge payload with existing file content
   * @returns Promise<string | null> The filename on success, null on failure
   */
  async save(
    payload: Partial<FileRecord> = {},
    id?: string | null,
    overwrite: boolean = false
  ): Promise<string | null> {
    try {
      const filesLib = await this.getFiles();

      // ID parameter takes precedence, then payload.id, then timestamp
      let fileId: string;
      if (id) {
        fileId = this.sanitizeFileId(id);
      } else if ('id' in payload && payload.id !== undefined) {
        fileId = String(payload.id);
      } else {
        fileId = String(new Date().getTime());
      }

      const filepath = `${this.filepath}/${fileId}.json`;

      const existingFile = await filesLib.list(filepath);
      if (existingFile.length) {
        if (overwrite) {
          // Overwrite mode: Replace entirely with payload (no merge)
          console.log(`Overwriting existing file: ${filepath}`);
          payload = {
            id: fileId,
            ...payload,
          };
        } else {
          // Default mode: Merge with existing content
          const buffer = await filesLib.read(filepath);
          const existingData = JSON.parse(buffer.toString());

          payload = { ...existingData, ...payload };
        }
        await filesLib.delete(filepath);
      } else {
        payload = {
          id: fileId,
          ...payload,
        };
      }

      // Remove createdAt and updatedAt from payload before saving
      // (these will be added from file system properties when loading)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { createdAt, updatedAt, ...payloadWithoutTimestamps } = payload as FileRecord;

      // Write the file
      await filesLib.write(filepath, JSON.stringify(payloadWithoutTimestamps));

      return fileId;
    } catch (error) {
      console.error('Error saving file:', error);
      return null;
    }
  }

  /**
   * Deletes files by their IDs
   * @param ids - Array of file IDs to delete
   * @returns Promise<FileRecord[]> Updated list of remaining files
   */
  async delete(ids: string[] = []): Promise<FileRecord[]> {
    const filesLib = await this.getFiles();

    for (const id of ids) {
      await filesLib.delete(`${this.filepath}/${id}.json`);
    }

    return await this.list();
  }

  /**
   * Sanitizes the file ID to contain only alphanumeric characters and underscores
   * @param id - The ID to sanitize
   * @returns Sanitized ID with invalid characters replaced by underscores
   */
  private sanitizeFileId(id: string): string {
    if (!id || typeof id !== 'string') {
      return String(new Date().getTime());
    }

    // Replace any non-alphanumeric characters (except underscore) with underscore
    const sanitized = id.replace(/[^a-zA-Z0-9_]/g, '_');

    // If the sanitized ID is empty or only underscores, use timestamp
    if (!sanitized || /^_+$/.test(sanitized)) {
      return String(new Date().getTime());
    }

    return sanitized;
  }

  /**
   * Initializes and returns the Files library instance
   * @returns Promise<any> Initialized Files library instance
   */
  private async getFiles(): Promise<any> {
    if (!this.files) {
      this.files = await Files.init();
    }
    return this.files;
  }
}

export default FileRepository;
